import React, { memo, useCallback, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useFlags } from "launchdarkly-react-client-sdk";

import { FMSLogger } from "FMSLogger";
import {
  cancelMission,
  changeMissionOrder,
  getActiveTab,
  getAllMissionsCount,
  getCompletedMissionsCount,
  getDeployedMissionsCount,
  getHeaderData,
  getMissions,
  getMissionRowsLimit,
  getMissionPlannerPeriod,
  resetPagination,
  setMissionRowsLimit,
  getCurrentFleetId,
  pauseMissionsUpdates,
  continueMissionsUpdates,
} from "Slices/missionPlanner";
import { getVehicles } from "Slices/vehicles";
import { MISSION_DETAILS_MODAL, toggleModal } from "Slices/modals";
import { MISSION_PLANNER_ACTIONS } from "Utils/constants";

import { List } from "../../../NewList";
import { RowData } from "../../../NewList/types";
import {
  CounterStyled,
  PaginationBodyStyled,
  PaginationButton,
  ShowingItemStyled,
} from "./styles";
import { ReactComponent as SelectIcon } from "../../assets/selectIconBold.svg";
import {
  getLoopsField,
  getStopsField,
  getMissionStatusField,
  getVdaActionsField,
} from "./utils";

const missionPlannerActionsLogger = FMSLogger.byPrefix(MISSION_PLANNER_ACTIONS);

const UNCANCELABLE_CODES = [
  "CANCELED",
  "FINISHED",
  "CANCEL_PENDING",
  "CANCEL_SENT",
  "FAILURE",
];

type MissionPlannerRowData = RowData & {
  missionStatusCode: MissionStatusCode;
  order: number;
  vehicleId: string;
};

export const MissionPlannerLists = memo(() => {
  const dispatch = useDispatch();
  const { featureFlagVdaAction } = useFlags();

  const rawMissions = useSelector(getMissions);
  const vehicles: Array<Vehicle> = useSelector(getVehicles);
  const baseHeaderData = useSelector(getHeaderData);
  const activeTab = useSelector(getActiveTab);
  const limit = useSelector(getMissionRowsLimit);
  const allMissionsTabCount = useSelector(getAllMissionsCount);
  const deployedMissionsTabCount = useSelector(getDeployedMissionsCount);
  const completedMissionsTabCount = useSelector(getCompletedMissionsCount);
  const currentFleetId = useSelector(getCurrentFleetId);
  const period = useSelector(getMissionPlannerPeriod);

  // Dynamic header data based on feature flag
  const headerData = useMemo(() => {
    const headers = { ...baseHeaderData };
    if (!featureFlagVdaAction) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { vdaActions, ...headersWithoutVdaActions } = headers;
      return headersWithoutVdaActions;
    }
    return headers;
  }, [baseHeaderData, featureFlagVdaAction]);

  const options = useMemo(
    () => [
      {
        name: "Cancel",
        isHide: (item: MissionPlannerRowData) =>
          UNCANCELABLE_CODES.includes(item.missionStatusCode),
        callback: (item: MissionPlannerRowData) => {
          missionPlannerActionsLogger.debug(
            `Pressed cancel mission with id:${item.id}`
          );
          dispatch(
            cancelMission({
              missionId: item.id,
              vehicleId: item.vehicleId,
            })
          );
        },
      },
    ],
    []
  );

  const preparedMissionsData: Array<MissionPlannerRowData> = useMemo(() => {
    missionPlannerActionsLogger.debug("Recalculate preparedMissionsData");
    if (!rawMissions) return [];
    return rawMissions.map((mission) => {
      // Backend sends "version" field from QueuedMissionSerializer.get_version()
      const versionNumber = (mission as any).version || 1;

      const baseData = {
        id: mission.id,
        name: mission.name,
        version: `v${versionNumber}`,
        assignedTo:
          vehicles.find((vehicle) => vehicle?.id === mission.vehicle)?.name ??
          "NONE",
        status: getMissionStatusField(mission.status),
        missionStatusCode: mission.status,
        stops: getStopsField(mission.passedStops, mission.totalStops),
        loops: getLoopsField(mission.passedLoops, mission.totalLoops),
        vehicleId: mission.vehicle,
        order: mission.order,
      };

      // Conditionally add vdaActions field based on feature flag
      if (featureFlagVdaAction) {
        return {
          ...baseData,
          vdaActions: getVdaActionsField(mission, dispatch),
        };
      }

      return baseData;
    });
  }, [rawMissions, vehicles, featureFlagVdaAction]);

  const handleRowClick = useCallback(
    (row: MissionPlannerRowData) => {
      missionPlannerActionsLogger.debug("Row clicked", { missionId: row.id });
      dispatch(
        toggleModal({
          type: MISSION_DETAILS_MODAL,
          data: { missionId: row.id },
        })
      );
    },
    [dispatch]
  );

  const handleDragStart = useCallback(() => {
    missionPlannerActionsLogger.debug("HandleDragStart");
    dispatch(pauseMissionsUpdates());
  }, []);

  const handleDragEnd = useCallback(
    async (
      isElementPlacedOnSamePosition: boolean,
      draggedMissionId: string,
      shiftedMission: MissionPlannerRowData,
      cancelAction: () => Array<MissionPlannerRowData>,
      updatedItems: Array<MissionPlannerRowData>
    ) => {
      missionPlannerActionsLogger.debug("HandleDragEnd", {
        isElementPlacedOnSamePosition,
        id: draggedMissionId,
        order: shiftedMission.order,
        updatedItemsIds: updatedItems.map((item) => item.id),
      });
      if (!isElementPlacedOnSamePosition) {
        await dispatch(
          changeMissionOrder({
            missionId: draggedMissionId,
            order: shiftedMission.order,
            cancelAction,
            newMissionsOrder: updatedItems,
          })
        );
      }
      dispatch(continueMissionsUpdates());
    },
    []
  );

  const isDragAndDropActive = useMemo(() => {
    if (!rawMissions) return false;

    const isSameVehicleForAllMissions = rawMissions.every(
      (mission) => mission.vehicle === rawMissions[0].vehicle
    );

    const isDeployedTab = activeTab === "Deployed";

    return isDeployedTab && isSameVehicleForAllMissions;
  }, [activeTab, rawMissions]);

  const currentActiveTabCount = useMemo(() => {
    if (activeTab === "Deployed") {
      return deployedMissionsTabCount;
    } else if (activeTab === "Completed") {
      return completedMissionsTabCount;
    } else return allMissionsTabCount;
  }, [
    activeTab,
    allMissionsTabCount,
    deployedMissionsTabCount,
    completedMissionsTabCount,
  ]);

  const Pagination = useMemo(() => {
    const paginationClickHandler = () => {
      const currentLimit = limit + 20;
      const paginationLimit =
        currentLimit <= currentActiveTabCount
          ? currentLimit
          : currentActiveTabCount;
      dispatch(setMissionRowsLimit(paginationLimit));
    };

    return (
      limit <= currentActiveTabCount && (
        <PaginationBodyStyled>
          <ShowingItemStyled>
            Showing:
            <CounterStyled>
              {limit > currentActiveTabCount ? currentActiveTabCount : limit}
            </CounterStyled>
            of
            <CounterStyled>{currentActiveTabCount}</CounterStyled>
          </ShowingItemStyled>
          {limit < currentActiveTabCount && (
            <PaginationButton onClick={paginationClickHandler}>
              Load More
              <SelectIcon />
            </PaginationButton>
          )}
        </PaginationBodyStyled>
      )
    );
  }, [limit, currentActiveTabCount]);

  useEffect(() => {
    dispatch(resetPagination());
  }, [activeTab, period, currentFleetId]);

  const optionsDropdownOffset = useMemo(() => ({ top: 40, right: 0 }), []);

  return (
    <List
      isDraggable={true}
      isDragAndDropActive={isDragAndDropActive}
      data={preparedMissionsData}
      headerData={headerData}
      options={options}
      pagination={Pagination}
      handleDragStart={handleDragStart}
      handleDragEnd={handleDragEnd}
      optionsDropdownOffset={optionsDropdownOffset}
      onRowClick={handleRowClick}
    />
  );
});
